<script setup lang="ts">
import { Card, CardContent, CardHeader, CardTitle } from "@/components/landingPage/ui/card";

import {
  Sparkle,
  Tag,
  Paintbrush,
  Blocks,
  LineChart,
  Wallet,
} from "lucide-vue-next";

interface BenefitsProps {
  icon: string;
  title: string;
  description: string;
}

const benefitList: BenefitsProps[] = [
  {
    icon: "sparkle",
    title: "Connaissance de soi",
    description:
      "Découvrez vos forces, valeurs et besoins à travers des activités ludiques et bienveillantes adaptées à votre rythme.",
  },
  {
    icon: "lineChart",
    title: "Insertion professionnelle",
    description:
      "Construisez votre projet professionnel avec des recommandations personnalisées de métiers, formations et entreprises inclusives.",
  },
  {
    icon: "blocks",
    title: "Accompagnement expert",
    description:
      "Bénéficiez du soutien d'une équipe pluridisciplinaire : psychologues TSA, conseillers en insertion et éducateurs spécialisés.",
  },
  {
    icon: "wallet",
    title: "Sensibilisation entreprises",
    description:
      "Créez des ponts entre talents neurodivergents et entreprises sensibilisées à l'inclusion et à la neurodiversité.",
  },
];

const iconMap: Record<
  string,
  | typeof Sparkle
  | typeof Tag
  | typeof Paintbrush
  | typeof Blocks
  | typeof LineChart
  | typeof Wallet
> = {
  sparkle: Sparkle,
  tag: Tag,
  paintbrush: Paintbrush,
  blocks: Blocks,
  lineChart: LineChart,
  wallet: Wallet,
};
</script>

<template>
  <section
    id="benefits"
    class="container mx-auto py-24 sm:py-32"
  >
    <div class="grid lg:grid-cols-2 place-items-center lg:gap-24">
      <div>
        <h2 class="text-lg text-[#ff9500] mb-2 tracking-wider">Notre Mission</h2>

        <h2 class="text-3xl md:text-4xl font-bold mb-4">
          Inclusion - Orientation - Découverte
        </h2>
        <p class="text-xl text-muted-foreground mb-8">
          Autrement Capable est une plateforme digitale inclusive qui valorise les forces 
          cognitives et émotionnelles des personnes neurodivergentes pour construire un avenir 
          professionnel choisi et réduire la désinsertion.
        </p>
      </div>

      <div class="grid lg:grid-cols-2 gap-4 w-full">
        <Card
          v-for="({ icon, title, description }, index) in benefitList"
          :key="title"
          class="bg-muted/50 dark:bg-card hover:bg-background dark:hover:bg-background transition-all delay-75 group/number"
        >
          <CardHeader>
            <div class="flex justify-between">
              <component
                class="size-8 mb-6 text-[#ff9500]"
                :is="iconMap[icon]"
              />

              <span
                class="text-5xl text-muted-foreground/15 font-medium transition-all delay-75 group-hover/number:text-muted-foreground/30"
                >0{{ index + 1 }}</span
              >
            </div>

            <CardTitle>{{ title }}</CardTitle>
          </CardHeader>

          <CardContent class="text-muted-foreground">
            {{ description }}
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
</template>
